import React, { useState } from 'react';

export interface HillTooltipProps {
  label: string;
  description: string;
  footnote?: string;
  labelColor?: string;
  iconColor?: string;
}

export function HillTooltip({ label, description, footnote, labelColor = 'text-gray-500', iconColor = 'border-gray-400 text-gray-400' }: HillTooltipProps): React.JSX.Element {
  const [mouse, setMouse] = useState<{ x: number; y: number } | null>(null);
  return (
    <div className="inline-flex items-center gap-1">
      <span className={`text-xs uppercase tracking-widest ${labelColor}`}>{label}</span>
      <span
        className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center cursor-default shrink-0 ${iconColor}`}
        style={{ fontSize: 8, lineHeight: 1 }}
        onMouseEnter={(e) => setMouse({ x: e.clientX, y: e.clientY })}
        onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => setMouse(null)}
      >?</span>
      {mouse && (
        <BioacousticsTooltip x={mouse.x} y={mouse.y} boxStyle={{ whiteSpace: 'normal', maxWidth: 288, lineHeight: '1.5' }}>
          <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 13 }}>
            {label} <span style={{ fontWeight: 400, opacity: 0.5 }}>(Hill q=1)</span>
          </p>
          <p style={{ margin: 0, opacity: 0.8, fontSize: 11 }}>{description}</p>
          {footnote && (
            <p style={{ margin: '8px 0 0', paddingTop: 8, borderTop: '1px solid var(--color-brand-teal-dark-200)', opacity: 0.6, fontSize: 11 }}>{footnote}</p>
          )}
        </BioacousticsTooltip>
      )}
    </div>
  );
}


interface BioacousticsTooltipProps {
  x: number;
  y: number;
  children: React.ReactNode;
  boxStyle?: React.CSSProperties;
}

export function BioacousticsTooltip({ x, y, children, boxStyle }: BioacousticsTooltipProps): React.JSX.Element {
  return (
    <div
      style={{
        position: 'fixed',
        ...(x > window.innerWidth / 2
          ? { right: window.innerWidth - x + 14 }
          : { left: x + 14 }),
        ...(y > window.innerHeight / 2
          ? { bottom: window.innerHeight - y + 10 }
          : { top: y - 10 }),
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-brand-soft-gray)',
          border: '1px solid var(--color-brand-teal-dark-200)',
          borderRadius: 4,
          padding: '8px 12px',
          fontSize: 12,
          lineHeight: '18px',
          color: 'var(--color-brand-teal-dark-500)',
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          maxWidth: 220,
          ...boxStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}
